import {
    CheckCircleFilled,
    ClockCircleFilled,
    ExclamationCircleFilled,
    MinusCircleOutlined,
    QuestionCircleFilled,
    WarningFilled,
} from '@ant-design/icons';
import {theme, Tooltip} from 'antd';
import React from 'react';

import {
    AdminListingReason,
    AdminListingSeverity,
    AdminProductListAnalytics,
    AdminProductStatus,
} from '../../../../gen/api/models';
import {marketplaceLabel} from '../../../helpers/status';
import {
    formatAgo,
    formatPercent,
    REASON_LABEL,
    SEVERITY_DESCRIPTION,
    SEVERITY_LABEL,
    severityTone,
} from '../../dashboard/helpers/slowSelling';

/** Severities whose gap to the suggested price is worth showing. */
const PRICED_ABOVE = new Set<AdminListingSeverity>([
    AdminListingSeverity.ListingSeverityOverpriced,
    AdminListingSeverity.ListingSeverityNotCompetitive,
]);

interface PriceAnalyticsIconProps {
    status: AdminProductStatus;
    analytics?: AdminProductListAnalytics;
}

/**
 * Price analytics verdict of an active product: OK, a problem, or not
 * analyzed yet. Nothing for other statuses: their prices are not analyzed.
 */
export const PriceAnalyticsIcon: React.FC<PriceAnalyticsIconProps> = ({status, analytics}) => {
    const {token} = theme.useToken();
    if (status !== AdminProductStatus.ProductStatusActive) {
        return null;
    }

    if (!analytics) {
        return (
            <Tooltip title="Prices not analyzed yet: the worker analyzes active products with a marketplace listing.">
                <MinusCircleOutlined style={{color: token.colorTextQuaternary}} />
            </Tooltip>
        );
    }

    const severity = analytics.severity as AdminListingSeverity;
    const tone = severityTone(severity);
    const style = {
        color: {
            error: token.colorError,
            warning: token.colorWarning,
            muted: token.colorTextTertiary,
            success: token.colorSuccess,
        }[tone],
    };
    let icon: React.ReactNode;
    if (tone === 'success') {
        icon = <CheckCircleFilled style={style} />;
    } else if (tone === 'error') {
        icon = <ExclamationCircleFilled style={style} />;
    } else if (tone === 'warning') {
        icon = <WarningFilled style={style} />;
    } else if (severity === AdminListingSeverity.ListingSeverityStale) {
        icon = <ClockCircleFilled style={style} />;
    } else {
        icon = <QuestionCircleFilled style={style} />;
    }

    const title = (
        <div>
            <div style={{marginBottom: 4}}>
                {tone === 'success' ? 'Prices are in line with the market' : SEVERITY_DESCRIPTION[severity]}
            </div>
            {analytics.listings.map((listing, i) => {
                const listingSeverity = listing.severity as AdminListingSeverity;
                const reasons = listing.reasons
                    .map((reason) => REASON_LABEL[reason as AdminListingReason])
                    .filter(Boolean)
                    .join(', ');
                return (
                    <div key={i}>
                        {marketplaceLabel(listing.marketplace)}: {SEVERITY_LABEL[listingSeverity] ?? listing.severity}
                        {PRICED_ABOVE.has(listingSeverity) &&
                        listing.gapRatio !== undefined &&
                        listing.gapRatio !== null
                            ? ` ${formatPercent(listing.gapRatio)}`
                            : null}
                        {reasons ? ` · ${reasons}` : null}
                    </div>
                );
            })}
            <div style={{marginTop: 4, opacity: 0.65}}>Analyzed {formatAgo(analytics.computedAt)}</div>
        </div>
    );

    return <Tooltip title={title}>{icon}</Tooltip>;
};
